import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltProductCardSlider — Product card image carousel.
 *
 * Desktop (pointer:fine): slides stack via CSS grid, opacity crossfade on hover,
 * arrow buttons navigate with class-based active state.
 *
 * Mobile: CSS scroll-snap + native swipe, IntersectionObserver updates bar indicators.
 *
 * @example
 * <shpflt-product-card-slider data-shpflt>
 *   <button class="product-card__nav product-card__nav--prev" aria-hidden="true"></button>
 *   <div class="product-card__slides" ref="track">
 *     <div class="product-card__slide">...</div>
 *   </div>
 *   <button class="product-card__nav product-card__nav--next" aria-hidden="true"></button>
 *   <div class="product-card__bars" ref="bars" aria-hidden="true">
 *     <span class="product-card__bar is-active"></span>
 *   </div>
 * </shpflt-product-card-slider>
 */
class ShpfltProductCardSlider extends ShopigurusHTMLElement {
  /** @type {IntersectionObserver | null} */
  #slideObserver = null;

  /** @type {number} */
  #currentIdx = 0;

  connectedCallback() {
    super.connectedCallback();

    const track = this.refs.track;
    const barsContainer = this.refs.bars;

    if (!track) return;

    const slides = Array.from(track.querySelectorAll('.product-card__slide'));
    const isDesktop = window.matchMedia('(pointer: fine)').matches;

    // On desktop, CSS sets all slides to opacity:0. Always mark the first slide
    // active so single-image products aren't invisible.
    if (isDesktop && slides.length > 0) {
      slides[0].classList.add('is-active');
    }

    if (slides.length < 2) return;

    const bars = barsContainer ? Array.from(barsContainer.children) : [];
    const signal = this.abortController.signal;

    /**
     * Navigates to the slide at the given index.
     *
     * Desktop crossfade logic:
     * 1. Clean up previous is-outgoing (hidden under current slide anyway)
     * 2. Move current slide from is-active → is-outgoing (stays visible underneath)
     * 3. Add is-active to incoming (fades in on top via z-index)
     *
     * The outgoing stays visible during the fade so no white gap appears.
     * It gets cleaned up on the next goTo call.
     *
     * @param {number} idx
     */
    const goTo = (idx) => {
      const newIdx = Math.max(0, Math.min(idx, slides.length - 1));
      if (newIdx === this.#currentIdx) return;

      if (isDesktop) {
        // Clean up all previous states
        for (const slide of slides) {
          slide.classList.remove('is-outgoing');
          slide.style.zIndex = '';
        }

        // Outgoing: keep visible underneath (no transition)
        slides[this.#currentIdx].classList.remove('is-active');
        slides[this.#currentIdx].classList.add('is-outgoing');

        // Incoming: fade in on top
        slides[newIdx].classList.add('is-active');
        slides[newIdx].style.zIndex = '1';
      } else {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({
          left: newIdx * track.offsetWidth,
          behavior: reducedMotion ? 'instant' : 'smooth',
        });
      }

      this.#currentIdx = newIdx;
      bars.forEach((bar, i) => bar.classList.toggle('is-active', i === newIdx));
    };

    if (isDesktop) {
      slides[0].classList.add('is-active');

      this.addEventListener('pointerenter', () => goTo(1), { signal });
      this.addEventListener('pointerleave', () => goTo(0), { signal });
    } else {
      if (bars.length > 0) {
        this.#slideObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const idx = slides.indexOf(entry.target);
              if (idx === -1) continue;
              this.#currentIdx = idx;
              bars.forEach((bar, i) => bar.classList.toggle('is-active', i === idx));
            }
          },
          { root: track, threshold: 0.5 }
        );
        slides.forEach((slide) => this.#slideObserver.observe(slide));
      }
    }

    const prevBtn = this.querySelector('.product-card__nav--prev');
    const nextBtn = this.querySelector('.product-card__nav--next');

    if (prevBtn) {
      prevBtn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          goTo(this.#currentIdx - 1);
        },
        { signal }
      );
    }

    if (nextBtn) {
      nextBtn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          goTo(this.#currentIdx + 1);
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#slideObserver?.disconnect();
    this.#slideObserver = null;
  }
}

customElements.define('shpflt-product-card-slider', ShpfltProductCardSlider);
